import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import { Box, DialogContent, Grid, Typography, Avatar } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getPlanFeatureBalanceUsage,
  getBulkPresignedURLForPut,
  fileUploadToS3,
  IFileInfoDTO,
  IBulkFileUploadDTO,
  createBatchApi,
  sendResumeToCVParser,
  ICVBulkUploadRequest,
  ICVBulkUploadDTO,
  getSkillsSuggestions,
  getJobById,
} from 'wisestep-api';
import {
  FileTypeVariants,
  FileUploadVariants,
  WsAutoComplete,
  WsDialog,
  WsFileUpload,
  WsLocationSearch,
  WsTypography,
  WsButton,
  WsTypoVariants,
  WsColors,
  ControlledTextInput,
} from 'wisestep-base';
import * as yup from 'yup';

import { WsTagsFilter } from '../../../../components/filters/components';
import WsTeamFilter from '../../../../components/filters/components/WsTeamsFilter';
import { useTokenContext } from '../../../../context';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import UploadCandidatesStatus from './UploadCandidatesStatus';
import UploadDeviceHeader from './UploadDeviceHeader';
import UploadPopup from './UploadPopup';

interface FileProgress {
  file: File;
  fileName: string;
  progress: number;
  presignedURL: string;
  error?: string;
}
const UploadFromDevice = () => {
  const schema = yup.object().shape({
    locations: yup.array().min(1, ' '),
  });

  const methods = useForm<ICVBulkUploadDTO>({
    resolver: yupResolver(schema),
    defaultValues: {
      locations: [],
    },
  });

  const navigate = useNavigate();
  const { setValue, getValues, reset, handleSubmit } = methods;
  const token = useTokenContext();
  const [uploadLimitReached, setuploadLimitReached] = useState<boolean>();
  const [filesProgress, setFilesProgress] = useState<FileProgress[]>([]);
  const [skillList, setSkillList] = useState<string[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [params] = useSearchParams();
  const jobId = String(params.get('jobId') ?? '');
  const maxFileSize = 3;
  const onFileUpload = async (file: File[]) => {
    const response = await getPlanFeatureBalanceUsage([
      'CS_UPLOAD_AND_PARSE_CV_COUNT',
      'CS_UPLOAD_AND_PARSE_CV_TOTAL_SIZE',
    ]);
    const fileAllowed = response['CS_UPLOAD_AND_PARSE_CV_COUNT'];
    const totalSizeAllowed = response['CS_UPLOAD_AND_PARSE_CV_TOTAL_SIZE'];
    if (fileAllowed <= 0) {
      setuploadLimitReached(true);
      // vm.errorCode = 1;
    } else if (totalSizeAllowed <= 0) {
      setuploadLimitReached(true);
      // vm.errorCode = 2;
    } else {
      // setValidFiles((oldFiles) => [...oldFiles, ...file]);
      const progressArray = file.map((file) => {
        return {
          file: file,
          fileName: file.name,
          progress: 0,
          presignedURL: '',
        };
      });
      setFilesProgress((fileProgress) => [...fileProgress, ...progressArray]);
    }
  };
  const onFileRejection = async (file: FileRejection[]) => {
    const progressArray = file.map((file) => {
      let errorMessage = file.errors[0].message;
      const code = file.errors[0].code;
      if (code === 'file-too-large') {
        errorMessage = `File is larger than ${maxFileSize} MB`;
      }
      return {
        file: file.file,
        fileName: file.file.name,
        error: errorMessage,
        progress: 0,
        presignedURL: '',
      };
    });
    setFilesProgress((fileProgress) => [...fileProgress, ...progressArray]);
  };

  const onSkillInputKey = async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillList(result.data?.map((item) => item.skill));
    }
  };

  const onSkillSelection = (skillTags: string[]) => {
    setValue('skills', skillTags ?? []);
  };

  useEffect(() => {
    const getJob = async () => {
      if (jobId) {
        const job = await getJobById(jobId);
        setValue('mapToJobId', job.data.title + '(' + job.data.jobCode + ')');
      }
    };
    getJob();
  }, [jobId, setValue]);

  const onUploadStart = async () => {
    const validFiles = filesProgress
      .filter((fileData) => !fileData.error)
      .map((fileData) => fileData.file);
    if (validFiles.length == 0) {
      setNotification(warning("Upload your CV's to start importing to Wisestep"));
      return;
    }
    const processRequest: ICVBulkUploadDTO = {
      totalSelected: validFiles.length,
      importFrom: 'LOCAL',
      skills: getValues('skills'),
      tags: getValues('tags'),
      locations: getValues('locations'),
      team: getValues('team'),
    };
    if (jobId) {
      processRequest.mapToJobId = jobId;
    }
    const batchDetails = await createBatchApi(processRequest);
    const response = await uploadFileToS3(validFiles);
    const fileProgressData = [...filesProgress];
    const skills = getValues('skills');
    const tags = getValues('tags');
    const locations = getValues('locations');
    const teams = getValues('team');
    const uploadedFiles: ICVBulkUploadDTO[] = [];
    const fileUploads: Promise<AxiosResponse>[] = [];
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileUploadDetails = response.bulkFileList.filter((e) => e.fileName === file.name);
      let presignedUrl: IFileInfoDTO = {
        preSignedUrl: '',
        fileName: '',
      }; // response.bulkFileList[i];
      if (fileUploadDetails?.length > 0) {
        presignedUrl = fileUploadDetails[0];
      }
      const pos = fileProgressData.map((e) => e.fileName).indexOf(file.name);
      fileProgressData[pos].presignedURL = presignedUrl.preSignedUrl ?? '';
      setFilesProgress([...fileProgressData]);
      if (presignedUrl.preSignedUrl) {
        try {
          fileUploads.push(
            fileUploadToS3(file, presignedUrl.preSignedUrl, (event: any) => {
              fileProgressData[pos].progress = (100 * event.loaded) / event.total;
              setFilesProgress([...fileProgressData]);
            })
          );
        } catch (e) {
          fileProgressData[pos].error = 'Failed To Upload';
        }
      }
      setFilesProgress([...fileProgressData]);
    }
    await Promise.all(fileUploads);
    fileProgressData
      .filter((file) => !file.error && file.presignedURL)
      .map((fileData) =>
        uploadedFiles.push({
          id: batchDetails.data.id,
          batchId: batchDetails.data.id,
          batchCode: batchDetails.data.batchCode,
          resumeURL: fileData.presignedURL.split('?')[0],
          resumeFileName: fileData.file.name,
          skills: skills,
          tags: tags,
          locations: locations,
          team: teams,
          mapToJobId: jobId ?? '',
          fileSizeInBytes: fileData.file.size,
        })
      );

    const request: ICVBulkUploadRequest = {
      batchCode: batchDetails.data.batchCode,
      bulkUploadList: uploadedFiles,
    };
    await sendResumeToCVParser(request);
    setNotification(success('Resumes Uploaded'));
    setOpenSuccessModal(true);
  };

  const resetData = () => {
    reset();
    setFilesProgress([]);
    setSkillList([]);
    setOpenSuccessModal(false);
  };
  const onSubmit = () => onUploadStart();

  const onError = () => {
    if (isEmpty(getValues('locations'))) {
      setNotification(
        warning('Please select a country for which you are uploading the Candidates')
      );
    }
  };

  return (
    <Box bgcolor="white">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <UploadDeviceHeader />
          <Grid container rowSpacing={2} columnSpacing={2} p={4}>
            <Grid item xs={6}>
              <WsTeamFilter
                name="team"
                placeHolder="Search For Teams"
                label={'Teams'}
                requireTeam={true}
                multiple={false}
              />
            </Grid>
            <Grid item xs={6}>
              <WsTagsFilter placeHolder="Enter Tags, comma seprated" name="tags" label="Tags" />
            </Grid>
            <Grid item xs={6}>
              <WsAutoComplete
                onValueEnter={onSkillInputKey}
                onTagsChange={onSkillSelection}
                placeholder="Type to Select Skills"
                initialValue={[]}
                optionsInStringArray={skillList}
                multiple={true}
                freeSolo={true}
                name="skills"
                label="Skills"
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <WsLocationSearch
                size="small"
                placeHolder="Select the Country of Candidates Profiles"
                name="locations"
                variant="outlined"
                showMandatorySymbol={true}
                wisestepApi={true}
                token={token.token}
                authorization={token.auth}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextInput
                name="mapToJobId"
                labelText="Job"
                fullWidth
                id="mapToJobId"
                variant="outlined"
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} pb={2}>
              <UploadCandidatesStatus
                selected={filesProgress.length}
                allowed={filesProgress.filter((file) => !file.error).length}
                notAllowed={filesProgress.filter((file) => file.error)?.length ?? 0}
                success={filesProgress.filter((file) => !file.error && file.progress == 100).length}
                failed={filesProgress.filter((file) => file.error).length}
              />
            </Grid>
            <Grid item xs={12}>
              <WsFileUpload
                multiple={true}
                name="uploadCandidates"
                placeholderHelperText="(.doc/.docx/.pdf/.odt/.html/.rtf/.txt files <= 3MB)"
                variant={FileUploadVariants.APP}
                onUpload={onFileUpload}
                onFileRejected={onFileRejection}
                placeholderText="Upload Candidates"
                fileTypes={FileTypeVariants.RESUME}
                maxFileSize={maxFileSize}
                buttonText="Upload Candidate CV&rsquo;s"
              />
            </Grid>
            <Grid item xs={12} pb={2} textAlign="center">
              <UploadPopup
                disabled={!(filesProgress.length > 0) || uploadLimitReached}
                buttonType={'submit'}
                open={filesProgress.length > 0}
              >
                {filesProgress.map((file, index) => (
                  <>
                    <Grid
                      container
                      key={index}
                      px={2}
                      pt={2}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            wordWrap: 'break-word',
                          }}
                        >
                          {file.fileName}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      key={index}
                      pt={0.2}
                      px={2}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Grid item xs={11}>
                        <LinearProgress sx={{ p: 0 }} variant="determinate" value={file.progress} />
                      </Grid>
                      <Grid item xs={1} display="flex" justifyContent="center">
                        <Typography
                          sx={{
                            wordWrap: 'break-word',
                          }}
                        >{`${Math.round(file.progress)}%`}</Typography>
                      </Grid>
                      {file.error && (
                        <Grid item>
                          <Typography>{file.error}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                ))}
              </UploadPopup>
            </Grid>
          </Grid>
        </form>
        <WsDialog size="medium" openDialog={openSuccessModal}>
          <DialogContent>
            <Grid container justifyContent="center" spacing={2} p={4}>
              <Grid item xs={12} display="flex" justifyContent="center">
                <Avatar sx={{ backgroundColor: WsColors.TheBlues400 }}>
                  <CheckIcon />
                </Avatar>
              </Grid>
              <Grid xs={12} item display="flex" textAlign="center">
                <WsTypography variant={WsTypoVariants.TITLE}>
                  CVs are now uploaded to the workspace and may take some time to parse and process.
                  We will notify you once the CVs are processed
                </WsTypography>
              </Grid>
              <Grid item columnGap={2} display="flex">
                <WsButton onClick={resetData} type="button" size="large" variant="outlined">
                  Skip
                </WsButton>
                <WsButton
                  onClick={() => navigate('/import-candidates/bulk-upload-report')}
                  type="button"
                  size="large"
                  variant="outlined"
                >
                  View History
                </WsButton>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      </FormProvider>
    </Box>
  );
};

export default UploadFromDevice;

export const uploadFileToS3 = async (files: File[]) => {
  const fileDetailsArray: IFileInfoDTO[] = [];
  files.map((file) => {
    const fileDetails: IFileInfoDTO = {
      fileName: file.name,
      fileType: file.type,
    };
    fileDetailsArray.push(fileDetails);
  });

  const request: IBulkFileUploadDTO = {
    bulkFileList: fileDetailsArray,
  };

  const response = await getBulkPresignedURLForPut(request);
  const responseData: IBulkFileUploadDTO = response.data;
  return responseData;
};
